import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { selectCurrentApplication } from '../../store/slices/application.slice';
import MainLayout from '../../components/layout/MainLayout';
import traceApi from '../../services/api/trace.api';
import TimeRangeSelector, { TimeRange } from '../../components/common/TimeRangeSelector';
import AutoRefreshToggle, { AutoRefreshConfig } from '../../components/common/AutoRefreshToggle';

export default function TracesPage() {
  const { t } = useTranslation(['apm', 'common']);
  const navigate = useNavigate();
  const selectedAppId = useAppSelector(selectCurrentApplication);
  const [traces, setTraces] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalTraces, setTotalTraces] = useState(0);

  // Time range and auto-refresh
  const [timeRange, setTimeRange] = useState<TimeRange>({
    startTime: Date.now() - 60 * 60 * 1000, // Last hour
    endTime: Date.now(),
    rangeType: 'preset',
    presetValue: '1h',
  });
  const [autoRefresh, setAutoRefresh] = useState<AutoRefreshConfig>({
    enabled: false,
    interval: 30000, // 30 seconds
  });
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  useEffect(() => {
    setCurrentPage(1);
    loadTraces();
  }, [selectedAppId, statusFilter, timeRange, searchQuery]);

  useEffect(() => {
    loadTraces();
  }, [currentPage, pageSize]);

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefresh.enabled) return;

    const interval = setInterval(() => {
      // Update time range if using preset
      if (timeRange.rangeType === 'preset' && timeRange.presetValue) {
        const endTime = Date.now();
        let startTime = endTime;

        const ranges: Record<string, number> = {
          '15m': 15 * 60 * 1000,
          '1h': 60 * 60 * 1000,
          '6h': 6 * 60 * 60 * 1000,
          '12h': 12 * 60 * 60 * 1000,
          '24h': 24 * 60 * 60 * 1000,
          '7d': 7 * 24 * 60 * 60 * 1000,
          '30d': 30 * 24 * 60 * 60 * 1000,
        };

        startTime = endTime - (ranges[timeRange.presetValue] || ranges['1h']);

        setTimeRange({
          ...timeRange,
          startTime,
          endTime,
        });
      } else {
        // For custom ranges, just reload
        loadTraces();
      }
    }, autoRefresh.interval);

    return () => clearInterval(interval);
  }, [autoRefresh, timeRange]);

  const loadTraces = async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * pageSize;

      const response = await traceApi.getAll(selectedAppId || 'all', {
        startTime: timeRange.startTime,
        endTime: timeRange.endTime,
        status: statusFilter !== 'all' ? (statusFilter as 'ok' | 'error') : undefined,
        operationName: searchQuery || undefined,
        limit: pageSize,
        offset: offset
      });

      if (response.success) {
        setTraces(response.data);
        setTotalTraces(response.meta.total);
        setLastUpdated(new Date());
      }
    } catch (error) {
      console.error('Error loading traces:', error);
      setTraces([]);
      setTotalTraces(0);
    } finally {
      setLoading(false);
    }
  };


  const handleSearch = () => {
    setCurrentPage(1);
    loadTraces();
  };

  const getStatusColor = (status: string) => {
    return status === 'ok' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
      'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
  };

  const getDurationColor = (duration: number) => {
    if (duration < 200) return 'text-green-600 dark:text-green-400';
    if (duration < 1000) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  return (
    <MainLayout>
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('apm:traces.title')}</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">{t('apm:traces.subtitle')}</p>
        </div>
        <button
          onClick={() => navigate('/agents/apm/install')}
          className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-indigo-700"
        >
          <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          {t('apm:traces.setupAgent')}
        </button>
      </div>

      {/* Time Range Selector */}
      <div className="mb-6">
        <TimeRangeSelector
          value={timeRange}
          onChange={setTimeRange}
        />
      </div>

      {/* Auto Refresh Toggle */}
      <div className="mb-6">
        <AutoRefreshToggle
          value={autoRefresh}
          onChange={setAutoRefresh}
          onManualRefresh={loadTraces}
          lastUpdated={lastUpdated}
        />
      </div>

      {/* Filters */}
      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        <input
          type="text"
          placeholder={t('apm:traces.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
        >
          <option value="all">{t('apm:traces.filters.allStatus')}</option>
          <option value="ok">{t('apm:traces.filters.success')}</option>
          <option value="error">{t('apm:traces.filters.error')}</option>
        </select>
      </div>

      {/* Traces Table */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : traces.length === 0 ? (
          <div className="flex justify-center items-center py-12 text-gray-500 dark:text-gray-400">
            {t('apm:traces.noTracesFound')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('apm:traces.table.traceId')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('apm:traces.table.operation')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('apm:traces.table.duration')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('apm:traces.table.spans')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('apm:traces.table.status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('apm:traces.table.timestamp')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-900 dark:divide-gray-800">
                {traces.map((trace) => (
                  <tr
                    key={trace.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer"
                    onClick={() => navigate(`/apm/traces/${trace.application_id || selectedAppId}/${trace.trace_id}`)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900 dark:text-white">
                      {trace.trace_id.substring(0, 16)}...
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {trace.operation_name}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-semibold ${getDurationColor(Number(trace.duration_ms))}`}>
                      {trace.duration_ms}ms
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {trace.span_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex rounded px-2 py-1 text-xs font-semibold ${getStatusColor(trace.status)}`}>
                        {trace.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(Number(trace.timestamp)).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {traces.length > 0 && (
          <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-800 dark:bg-gray-800/50">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {t('apm:traces.pagination.showing', {
                  from: ((currentPage - 1) * pageSize) + 1,
                  to: Math.min(currentPage * pageSize, totalTraces),
                  total: totalTraces
                })}
              </span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="ml-4 rounded border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300"
              >
                <option value={10}>{t('apm:traces.pagination.perPage10')}</option>
                <option value={20}>{t('apm:traces.pagination.perPage20')}</option>
                <option value={50}>{t('apm:traces.pagination.perPage50')}</option>
              </select>
            </div>

            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="rounded border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                {t('apm:traces.pagination.first')}
              </button>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="rounded border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                {t('apm:traces.pagination.previous')}
              </button>
              <span className="flex items-center px-3 text-sm text-gray-700 dark:text-gray-300">
                {t('apm:traces.pagination.page', { current: currentPage, total: Math.ceil(totalTraces / pageSize) })}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage >= Math.ceil(totalTraces / pageSize)}
                className="rounded border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                {t('apm:traces.pagination.next')}
              </button>
              <button
                onClick={() => setCurrentPage(Math.ceil(totalTraces / pageSize))}
                disabled={currentPage >= Math.ceil(totalTraces / pageSize)}
                className="rounded border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                {t('apm:traces.pagination.last')}
              </button>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
